import { useState, useEffect } from "react";
import { Plus, Edit, Trash2 } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import FileUpload from "@/components/FileUpload";

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ image_url: "", link: "", order: 0 });

  useEffect(() => { fetchBanners(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBanners = async () => {
    const { data } = await api.get("/banners");
    setBanners(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, order: parseInt(form.order) || 0 };
    if (editing) {
      await api.put(`/banners/${editing}`, payload);
    } else {
      await api.post("/banners", payload);
    }
    setShowForm(false);
    setEditing(null);
    setForm({ image_url: "", link: "", order: 0 });
    fetchBanners();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this banner?")) {
      await api.delete(`/banners/${id}`);
      fetchBanners();
    }
  };

  const inputClass = "w-full bg-slate-50 border-b-2 border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <AdminLayout>
      <div data-testid="admin-banners-page">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Banners</h1>
          <button
            data-testid="add-banner-btn"
            onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ image_url: "", link: "", order: 0 }); }}
            className="bg-[#0284C7] text-white font-bold py-2 px-4 text-sm flex items-center gap-2 transition-none"
          >
            <Plus size={16} /> Add
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white p-4 mb-4 space-y-3" data-testid="banner-form">
            <FileUpload
              label="Banner Image"
              accept="image/*"
              uploadType="banners"
              currentUrl={form.image_url}
              onUpload={(url) => setForm({ ...form, image_url: url })}
            />
            <div className="mt-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Or paste Image URL</label>
              <input data-testid="banner-image-input" placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputClass} required />
            </div>
            <input data-testid="banner-link-input" placeholder="Link (optional)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className={inputClass} />
            <input data-testid="banner-order-input" placeholder="Order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={inputClass} />
            {form.image_url && (
              <div className="aspect-video w-full max-w-md bg-slate-200 overflow-hidden">
                <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" />
              </div>
            )}
            <button data-testid="banner-submit-btn" type="submit" className="bg-[#0284C7] text-white font-bold py-3 px-6 text-sm transition-none">
              {editing ? "Update" : "Create"} Banner
            </button>
          </form>
        )}

        <div className="space-y-2" data-testid="banners-list">
          {banners.map((banner) => (
            <div key={banner.id} className="bg-white p-3 flex gap-3 items-center">
              <div className="w-32 aspect-video bg-slate-200 flex-shrink-0 overflow-hidden">
                <img src={banner.image_url} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500 truncate">Order: {banner.order}</p>
                {banner.link && <p className="text-xs text-[#0284C7] truncate">{banner.link}</p>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setForm({ image_url: banner.image_url, link: banner.link || "", order: banner.order || 0 }); setEditing(banner.id); setShowForm(true); }} className="p-2 text-slate-500">
                  <Edit size={16} />
                </button>
                <button onClick={() => handleDelete(banner.id)} className="p-2 text-red-500">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
          {banners.length === 0 && <p className="text-sm text-slate-400 py-4">No banners yet.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
